import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, ChevronLeft, RefreshCw, Monitor, Globe, MessageSquare, Clock, CheckCircle, XCircle, AlertTriangle, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';
const CATEGORIES = ['Bug', 'Upload', 'Page', 'Performance', 'Other'];
const STATUSES = ['New', 'InProgress', 'Resolved', 'Closed'];

const STATUS_COLORS = {
  New: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  InProgress: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  Resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  Closed: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
};

const STATUS_ICONS = {
  New: AlertTriangle,
  InProgress: Clock,
  Resolved: CheckCircle,
  Closed: XCircle,
};

const AdminBugReports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ page, limit: 20 });
      if (filterStatus) params.append('status', filterStatus);
      if (filterCategory) params.append('category', filterCategory);

      const { data } = await axios.get(`${API}/admin/bug-reports?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(data.reports || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      toast.error('Failed to load bug reports');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterCategory]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Fetch toggle state
  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/public/settings`);
        setEnabled(data.bug_report_enabled === true);
      } catch { /* silent */ }
    };
    check();
  }, []);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(`${API}/admin/bug-reports/toggle`, { enabled: !enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnabled(data.bug_report_enabled);
      toast.success(`Bug reporting ${data.bug_report_enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to toggle');
    } finally {
      setToggling(false);
    }
  };

  const handleStatusChange = async (reportId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/bug-reports/${reportId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: newStatus } : r));
      if (selected?.id === reportId) setSelected(prev => ({ ...prev, status: newStatus }));
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/bug-reports/${selected.id}`, { admin_notes: adminNotes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(prev => prev.map(r => r.id === selected.id ? { ...r, admin_notes: adminNotes } : r));
      setSelected(prev => ({ ...prev, admin_notes: adminNotes }));
      toast.success('Notes saved');
    } catch (err) {
      toast.error('Failed to save notes');
    }
  };

  const openDetail = (report) => {
    setSelected(report);
    setAdminNotes(report.admin_notes || '');
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="text-zinc-400 hover:text-white transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <Bug className="w-5 h-5 text-orange-400" />
            <h1 className="text-lg font-bold">Bug Reports</h1>
            <span className="text-sm text-zinc-500">({total})</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleToggle}
              disabled={toggling}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-orange-500' : 'bg-zinc-700'}`}
              data-testid="bug-reports-toggle"
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-sm text-zinc-400">{enabled ? 'Accepting Reports' : 'Reporting OFF'}</span>
            <Button variant="ghost" size="sm" onClick={fetchReports} className="text-zinc-400 hover:text-white">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="bug-reports-filters">
          <Filter className="w-4 h-4 text-zinc-500" />
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            className="bg-zinc-800 border border-zinc-700 text-sm text-white rounded-lg px-3 py-1.5"
            data-testid="filter-status"
          >
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
            className="bg-zinc-800 border border-zinc-700 text-sm text-white rounded-lg px-3 py-1.5"
            data-testid="filter-category"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="flex gap-6">
          {/* Report list */}
          <div className={`flex-1 space-y-2 ${selected ? 'hidden sm:block sm:max-w-md' : ''}`}>
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent" />
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-16 text-zinc-500">
                <Bug className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No bug reports found</p>
              </div>
            ) : (
              <>
                {reports.map(r => {
                  const StatusIcon = STATUS_ICONS[r.status] || AlertTriangle;
                  return (
                    <Card
                      key={r.id}
                      className={`bg-zinc-900 border-zinc-800 hover:border-zinc-700 cursor-pointer transition-colors ${selected?.id === r.id ? 'border-orange-500/50' : ''}`}
                      onClick={() => openDetail(r)}
                      data-testid={`bug-report-row-${r.id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[r.status]}`}>
                                <StatusIcon className="w-3 h-3" />
                                {r.status}
                              </span>
                              <span className="text-xs text-zinc-600 bg-zinc-800 px-2 py-0.5 rounded">{r.category}</span>
                            </div>
                            <p className="text-sm text-zinc-300 truncate">{r.message}</p>
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-zinc-600">
                              <span>{r.userName || r.userEmail}</span>
                              <span>{formatDate(r.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {/* Pagination */}
                {pages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-4">
                    <Button
                      variant="ghost" size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                      className="text-zinc-400"
                    >Prev</Button>
                    <span className="text-sm text-zinc-500">{page} / {pages}</span>
                    <Button
                      variant="ghost" size="sm"
                      disabled={page >= pages}
                      onClick={() => setPage(p => p + 1)}
                      className="text-zinc-400"
                    >Next</Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <div className="flex-1 sm:max-w-xl" data-testid="bug-report-detail">
              <Card className="bg-zinc-900 border-zinc-800 sticky top-4">
                <CardContent className="p-5 space-y-5">
                  {/* Close on mobile */}
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-white">Report Detail</h3>
                    <button onClick={() => setSelected(null)} className="sm:hidden text-zinc-500 hover:text-white">
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Status</label>
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.map(s => (
                        <button
                          key={s}
                          onClick={() => handleStatusChange(selected.id, s)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            selected.status === s ? STATUS_COLORS[s] : 'border-zinc-700 text-zinc-500 hover:border-zinc-600'
                          }`}
                          data-testid={`status-btn-${s}`}
                        >{s}</button>
                      ))}
                    </div>
                  </div>

                  {/* User & Date */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-zinc-500 mb-1">User</label>
                      <p className="text-sm text-zinc-300">{selected.userName || selected.userEmail}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-zinc-500 mb-1">Date</label>
                      <p className="text-sm text-zinc-300">{formatDate(selected.createdAt)}</p>
                    </div>
                  </div>

                  {/* Category & Page */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-zinc-500 mb-1">Category</label>
                      <p className="text-sm text-zinc-300">{selected.category}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-zinc-500 mb-1">Page URL</label>
                      <p className="text-sm text-blue-400 truncate">{selected.page_url || '—'}</p>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">
                      <MessageSquare className="w-3 h-3 inline mr-1" />Message
                    </label>
                    <p className="text-sm text-zinc-200 bg-zinc-800/50 rounded-lg p-3 whitespace-pre-wrap">{selected.message}</p>
                  </div>

                  {/* Screenshot */}
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Screenshot</label>
                    {selected.screenshot_url ? (
                      <a href={selected.screenshot_url} target="_blank" rel="noopener noreferrer" className="block">
                        <img
                          src={selected.screenshot_url}
                          alt="Bug screenshot"
                          className="rounded-lg border border-zinc-700 max-h-48 w-auto cursor-pointer hover:border-zinc-500 transition-colors"
                          data-testid="screenshot-detail-img"
                        />
                        <p className="text-xs text-blue-400 mt-1 hover:underline">Click to open full size</p>
                      </a>
                    ) : (
                      <p className="text-xs text-zinc-600 italic">No screenshot attached.</p>
                    )}
                  </div>

                  {/* Metadata */}
                  {(selected.user_agent || selected.viewport) && (
                    <div>
                      <label className="block text-xs font-medium text-zinc-500 mb-1">
                        <Monitor className="w-3 h-3 inline mr-1" />Device Info
                      </label>
                      <div className="bg-zinc-800/50 rounded-lg p-3 text-xs text-zinc-400 space-y-1">
                        {selected.viewport?.width && (
                          <p>Viewport: {selected.viewport.width} x {selected.viewport.height}</p>
                        )}
                        {selected.user_agent && <p className="break-all">UA: {selected.user_agent}</p>}
                        {selected.app_version && <p>Version: {selected.app_version}</p>}
                      </div>
                    </div>
                  )}

                  {/* Admin Notes */}
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Admin Notes</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={3}
                      className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-2 text-sm resize-none focus:border-orange-500 focus:outline-none"
                      placeholder="Internal notes..."
                      data-testid="admin-notes-textarea"
                    />
                    <Button
                      size="sm"
                      onClick={handleSaveNotes}
                      className="mt-2 bg-zinc-700 hover:bg-zinc-600 text-white text-xs"
                      data-testid="save-notes-btn"
                    >Save Notes</Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBugReports;
